// src/constants/metrics.ts
import type { ColumnOption } from "@/components/common/ColumnPicker";

export const METRICS_PERCENTAGE_CHANGES_DASHBOARD_TIME_WINDOWS: string[] = [
  "1d",
  "7d",
  "30d",
  "60d",
  "90d",
  "180d",
  "365d",
] as const;

export type TimePeriod =
  (typeof METRICS_PERCENTAGE_CHANGES_DASHBOARD_TIME_WINDOWS)[number];

// Time windows for dashboard data
// Both API and UI now use lowercase with 'd' suffix (1d, 7d, 30d, 60d, 90d, 180d, 365d)
// Using day-based format (30d, 90d, 365d) instead of month format (1m, 3m, 1y) to avoid confusion with minutes
export const TIME_WINDOWS = {
  // API time window keys (used in API requests)
  API: ["1d", "7d", "30d", "60d", "90d", "180d", "365d"] as const,
  
  // UI time window keys (used in UI displays) - now all in days format
  UI: ["1d", "7d", "30d", "60d", "90d", "180d", "365d"] as const,
  
  // Mapping from UI keys to API keys (now identical)
  UI_TO_API: {
    "1d": "1d",
    "7d": "7d",
    "30d": "30d",
    "60d": "60d",
    "90d": "90d",
    "180d": "180d",
    "365d": "365d",
  } as const,
  
  // Mapping from API keys to UI keys (now identical)
  API_TO_UI: {
    "1d": "1d",
    "7d": "7d",
    "30d": "30d",
    "60d": "60d",
    "90d": "90d",
    "180d": "180d",
    "365d": "365d",
  } as const,
  
  // Short labels for display (all in days format)
  SHORT_LABELS: {
    "1d": "1d",
    "7d": "7d",
    "30d": "30d",
    "60d": "60d",
    "90d": "90d",
    "180d": "180d",
    "365d": "365d",
  } as const,
} as const;

// Global all-metrics (canonical ids). Labels are resolved from the catalog at render time.
export const ALL_METRICS_OPTIONS: ColumnOption[] = (
  [
    "price_close",
    "market_cap",
    "fdv",
    "volume",
    "dex_volume",
    "fees",
    "revenue",
    "tvl",
    "dau",
    "tx_count",
    // Derivatives
    "futures_oi",
    "futures_fr_oiw",
    "futures_fr_volw",
  ] as const
).map((id) => ({ id, label: id }));

// Global all-ratios (canonical ids)
export const ALL_RATIOS_OPTIONS: ColumnOption[] = (
  [
    "ratio:marketCapToAnnualisedFees",
    "ratio:marketCapToDexTradingVolume",
    "ratio:marketCapToTvl",
    "ratio:tvlToAnnualisedFees",
    "ratio:tvlToDexTradingVolume",
    "ratio:annualisedFeesToActiveAdresses",
    "ratio:annualisedFeesToTransactions",
    "ratio:annualisedTransactionsToAnnualizedActiveAddresses",
  ] as const
).map((id) => ({ id, label: id }));

// Centralized enum-like constants for metric keys (metrics + ratios)
export const MetricKeys = {
  PriceClose: "price_close",
  MarketCap: "market_cap",
  FDV: "fdv",
  Volume: "volume",
  DexVolume: "dex_volume",
  Fees: "fees",
  Revenue: "revenue",
  TVL: "tvl",
  DAU: "dau",
  TxCount: "tx_count",
  // Additional ratios/metrics
  Ratio_FdvToFees: "ratio:fdvToFees",
  // Ratios
  Ratio_MarketCapToAnnualisedFees: "ratio:marketCapToAnnualisedFees",
  Ratio_MarketCapToDexTradingVolume: "ratio:marketCapToDexTradingVolume",
  Ratio_MarketCapToTvl: "ratio:marketCapToTvl",
  Ratio_MarketCapToDau: "ratio:marketCapToDau",
  Ratio_MarketCapToTxCount: "ratio:marketCapToTxCount",
  Ratio_TvlToAnnualisedFees: "ratio:tvlToAnnualisedFees",
  Ratio_TvlToDexTradingVolume: "ratio:tvlToDexTradingVolume",
  Ratio_AnnualisedFeesToActiveAdresses: "ratio:annualisedFeesToActiveAdresses",
  Ratio_AnnualisedFeesToTransactions: "ratio:annualisedFeesToTransactions",
  Ratio_AnnualisedTransactionsToAnnualizedActiveAddresses: "ratio:annualisedTransactionsToAnnualizedActiveAddresses",
  Ratio_FeesToVolume: "ratio:feesToVolume",
  Ratio_TxPerUser: "ratio:txPerUser",
  Ratio_OIToVolume: "ratio:oiToVolume",
  Ratio_OIToTvl: "ratio:oiToTvl",
  // Portfolio risk metrics
  PortReturn: "port_return",
  Drawdown: "drawdown",
  Ret30d: "ret_30d",
  Ret90d: "ret_90d",
  Ret180d: "ret_180d",
  Ret365d: "ret_365d",
  Vol30d: "vol_30d",
  Vol90d: "vol_90d",
  Vol180d: "vol_180d",
  Vol365d: "vol_365d",
  Sharpe30d: "sharpe_30d",
  Sharpe90d: "sharpe_90d",
  Sharpe180d: "sharpe_180d",
  Sharpe365d: "sharpe_365d",
  Sortino30d: "sortino_30d",
  Sortino90d: "sortino_90d",
  Sortino180d: "sortino_180d",
  Sortino365d: "sortino_365d",
  MaxDrawdown90d: "max_drawdown_90d",
  MaxDrawdown365d: "max_drawdown_365d",
  ReturnsDailyTS: "returns_daily_ts",
  ES95_30d_Norm: "es95_30d_norm",
  VaR95_30d_Norm: "var95_30d_norm",
  BetaBTC90d: "beta_btc_90d",
  BetaETH90d: "beta_eth_90d",
  CorrBTC90d: "corr_btc_90d",
  CorrETH90d: "corr_eth_90d",
  AlphaBTC30d: "alpha_btc_30d",
  AlphaBTC90d: "alpha_btc_90d",
  HHI: "hhi",
  EffectiveNumHoldings: "effective_num_holdings",
  LargestPositionPct: "largest_position_pct",
  LargestCategoryPct: "largest_category_pct",
  PctNavTop1: "pct_nav_top1",
  PctNavTop3: "pct_nav_top3",
  PctNavTop5: "pct_nav_top5",
  PctNavTop10: "pct_nav_top10",
  PctNavHighVolume50m: "pct_nav_high_volume_50m",
  PctNavStablecoins: "pct_nav_stablecoins",
  Turnover30d: "turnover_30d",
  // Futures metrics
  FuturesOI: "futures_oi",
  FuturesFR: "futures_fr",
  FuturesFROIW: "futures_fr_oiw",
  FuturesFRVolW: "futures_fr_volw",
} as const;

export type MetricKey = typeof MetricKeys[keyof typeof MetricKeys];

// Charts: curated metric ids (volume first), removing metrics not desired for charts
export const CHART_METRICS_OPTIONS: ColumnOption[] = (
  [
    MetricKeys.Volume, // first
    MetricKeys.MarketCap,
    MetricKeys.FDV,
    MetricKeys.DexVolume,
    MetricKeys.Fees,
    MetricKeys.Revenue,
    MetricKeys.TVL,
    MetricKeys.DAU,
    MetricKeys.TxCount,
  ] as const
).map((id) => ({ id, label: id }));

// Charts: curated ratios (prefetched ratios first, then the rest)
export const CHART_RATIOS_OPTIONS: ColumnOption[] = (
  [
    // Prefetched ratios (first 4)
    MetricKeys.Ratio_MarketCapToAnnualisedFees,
    MetricKeys.Ratio_MarketCapToTvl,
    MetricKeys.Ratio_MarketCapToDexTradingVolume,
    MetricKeys.Ratio_AnnualisedFeesToTransactions,
    // Other ratios
    MetricKeys.Ratio_AnnualisedFeesToActiveAdresses,
    MetricKeys.Ratio_AnnualisedTransactionsToAnnualizedActiveAddresses,
    MetricKeys.Ratio_TvlToAnnualisedFees,
    MetricKeys.Ratio_TvlToDexTradingVolume,
  ] as const
).map((id) => ({ id, label: id }));

// Charts: curated derivatives metrics for the Derivatives tab
export const CHART_DERIVATIVES_OPTIONS: ColumnOption[] = (
  [
    MetricKeys.FuturesOI,
    MetricKeys.FuturesFROIW,
    MetricKeys.FuturesFRVolW,
  ] as const
).map((id) => ({ id, label: id }));

// Centralized enum-like constants for metric keys (metrics + ratios)
 
